// Mock data for World Wide Wholesale Deals LLC clone (frontend-only)
import {
  Package, FileText, Store, RotateCcw, Undo2,
  Clock, ShieldCheck, DollarSign, Headphones,
  ClipboardList, PhoneCall, FileCheck2, Truck, Wallet,
  Handshake, Percent, Zap, Globe, Eye, Network, Search,
  Factory, Boxes, Building2, Warehouse, Gavel,
} from "lucide-react";

export const COMPANY = {
  name: "World Wide Wholesale Deals LLC",
  shortName: "WWWD",
  phone: "646-698-1657",
  phoneHref: "tel:+16466981657",
  email: "Aaron@www-deals.com",
  website: "www.www-deals.com",
  address: "1407 Broadway, Suite 1810, Manhattan, New York 10018",
};

export const STATS = [
  { value: "28", label: "Years in Business" },
  { value: "34+", label: "Countries Shipped To" },
  { value: "1000s", label: "Pallets Shipped Each Year" },
];

export const SERVICES = [
  {
    id: "inventory",
    icon: Package,
    title: "Inventory Liquidation",
    short: "Clear out slow-moving stock, surplus units, and end-of-life products in one straightforward deal.",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBpbnZlbnRvcnl8ZW58MHx8fHwxNzg3MDkxNDQ1fDA&ixlib=rb-4.1.0&q=85",
    long: "Shelves packed with product that just won't turn? We buy it directly. Surplus units, aging stock, and phased-out lines are all fair game \u2014 wrapped up in a single, tidy purchase that frees your space and puts cash back in your pocket.",
  },
  {
    id: "bankruptcy",
    icon: FileText,
    title: "Corporate Bankruptcy Liquidation",
    short: "We collaborate with courts, trustees, and creditors to buy inventory and help resolve outstanding balances.",
    image: "https://images.unsplash.com/photo-1583521214690-73421a1829a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxsaXF1aWRhdGlvbiUyMGRvY3VtZW50c3xlbnwwfHx8fDE3ODcwOTE0NzV8MA&ixlib=rb-4.1.0&q=85",
    long: "Working with courts, trustees, and creditors, we purchase company inventory to help settle what's owed. We also step in early \u2014 guiding businesses through pre-bankruptcy planning so the outcome is smoother and recovery is as high as possible.",
  },
  {
    id: "closings",
    icon: Store,
    title: "Store Closings",
    short: "Full-service store-closing and pack-out programs built to squeeze the most value from leftover stock.",
    image: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBpbnZlbnRvcnl8ZW58MHx8fHwxNzg3MDkxNDQ1fDA&ixlib=rb-4.1.0&q=85",
    long: "Shutting a single shop or winding down a whole chain? We run the entire store and warehouse pack-out for you \u2014 handling crews, transport, and payout \u2014 so you can close the door with cash in hand and none of the headaches.",
  },
  {
    id: "rejected",
    icon: Undo2,
    title: "Rejected & Abandoned Inventory",
    short: "When someone walks away from their goods or a shipment gets turned down, we're ready to buy.",
    image: "https://images.pexels.com/photos/29653988/pexels-photo-29653988.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    long: "If a supplier or customer abandons a load, or product gets refused at the dock, we'll take it off your hands. We move fast on the paperwork and logistics so what looked like a write-off turns into real recovered value.",
  },
  {
    id: "returns",
    icon: RotateCcw,
    title: "Customer Returns & Reverse Logistics",
    short: "End-to-end reverse-logistics programs that trim costs and pull profit back out of returns.",
    image: "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    long: "Returns stack up quickly and eat into margin. Our reverse-logistics programs take the burden off your team, lowering handling costs while recapturing profit from returned, refurbished, and open-box goods.",
  },
];

export const WORK_WITH = [
  "Manufacturers, Wholesalers & Retailers",
  "3PL Warehouses, Fulfillment Centers & Cargo Companies",
  "Insurance Salvage Claims, Bankruptcies & Warehouseman\u2019s Liens",
];

// Powerful "Who we work with" grid
export const WHO_WE_WORK_WITH = [
  { icon: Factory, title: "Manufacturers", text: "Move overruns and end-of-run production without touching your primary accounts." },
  { icon: Boxes, title: "Wholesalers", text: "Clear surplus lots and free up warehouse space for faster-moving SKUs." },
  { icon: Truck, title: "Distributors", text: "Offload aging and discontinued stock while keeping your channels protected." },
  { icon: Store, title: "Retailers", text: "Turn shelf pulls, seasonal leftovers, and closeouts into immediate capital." },
  { icon: Building2, title: "Factories", text: "Recover value from excess raw runs, seconds, and canceled orders at scale." },
  { icon: Warehouse, title: "3PL Warehouses", text: "Resolve abandoned, unclaimed, and aged inventory sitting in your facilities." },
  { icon: ShieldCheck, title: "Insurance Salvage Claims", text: "Fast, reliable buyouts on salvage and recovery loads of nearly any category." },
  { icon: Gavel, title: "Bankruptcies & Warehouseman\u2019s Liens", text: "We work with courts, trustees, and creditors to liquidate assets and settle balances." },
];

export const ABOUT_POINTS = [
  "Asset Recovery Service",
  "Store & Warehouse Pack-Outs",
  "Alternative Markets Distribution",
  "Retail Profit-Share Program",
];

export const FEATURES = [
  { icon: Clock, title: "Industry-Leading Speed", text: "Our team reviews inventory opportunities around the clock, so we can act as fast as your timeline demands." },
  { icon: ShieldCheck, title: "Complete Brand Protection", text: "We relocate product quietly through offline channels, keeping your core sales lanes untouched." },
  { icon: DollarSign, title: "Immediate Payment", text: "Payment is made in full by wire transfer the moment goods land at one of our warehouses." },
  { icon: Headphones, title: "Around-the-Clock Support", text: "Get in touch by email, phone, live chat, or WhatsApp \u2014 any hour, any day of the week." },
];

export const PROCESS = [
  { n: "01", icon: ClipboardList, title: "Send Your List", text: "Share your inventory file \u2014 a spreadsheet works best \u2014 with as much product detail as you can." },
  { n: "02", icon: PhoneCall, title: "Free Consultation", text: "One of our specialists reaches out by phone or email to learn more about your stock and goals." },
  { n: "03", icon: FileCheck2, title: "Get an Offer", text: "If it's a match, you'll receive an offer \u2014 and once you accept, a purchase order follows." },
  { n: "04", icon: Truck, title: "Arrange Pickup", text: "Our logistics crew schedules collection at your warehouse with one of our 53\u2019 trailers." },
  { n: "05", icon: Wallet, title: "Receive Payment", text: "As soon as your inventory reaches our facility, funds are wired to you \u2014 no waiting around." },
];

export const CATEGORIES = [
  "Apparel", "Beauty & Cosmetics", "Food & Beverage", "Electronics", "Detergents",
  "Furniture", "Handbags", "Luggage", "Plastics", "Gifts", "Pet Supplies",
  "General Merchandise", "Craft", "Toys", "Garden", "Sporting Goods", "Seasonal",
  "Home Goods", "Housewares", "Kitchenware", "Bed & Bath", "Paper Products",
  "Footwear", "Hardlines", "Softlines", "Textiles", "Automotive",
];

export const TESTIMONIALS = [
  {
    name: "Ron B.",
    role: "Director of Operations",
    text: "We signed two deals with them \u2014 a straight purchase for part of the load and a consignment arrangement for the rest. All we had to do was palletize and load the trucks they sent. Hundreds of pallets later, it was done, and the funds hit our account within a day of invoicing. Genuinely impressive.",
  },
  {
    name: "Michael R.",
    role: "President",
    text: "A pleasure to deal with from start to finish. They clearly know the market inside out and have the connections to back it up. Everything was handled thoughtfully.",
  },
  {
    name: "Brittany L.",
    role: "Sr. Business Development",
    text: "The whole transaction felt effortless \u2014 organized, well communicated, and completely above board. We were paid on time and to the penny. I'd point anyone their way.",
  },
  {
    name: "David E. R. III",
    role: "VP of Logistics",
    text: "What stood out was how quickly and accurately they handled serious volume. No two projects were the same, yet they worked through every wrinkle and got us to a clean result.",
  },
  {
    name: "Nicole H.",
    role: "Retail Operations",
    text: "They laid out the process and expectations up front and then delivered on every one. The professionalism and follow-through were a breath of fresh air compared to most vendors I deal with.",
  },
];

export const BLOG_POSTS = [
  {
    id: 1,
    title: "When Should You Actually Liquidate Excess Stock?",
    excerpt: "Aging product quietly drains cash and clogs your warehouse. Here are the telltale signals that it's time to turn that stock back into usable capital.",
    category: "Inventory",
    date: "Jun 18, 2025",
    image: "https://images.unsplash.com/photo-1684695749267-233af13276d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjBpbnZlbnRvcnl8ZW58MHx8fHwxNzg3MDkxNDQ1fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 2,
    title: "Reverse Logistics: Getting Profit Back From Returns",
    excerpt: "Returns will always happen \u2014 but writing them off shouldn't. A well-run reverse-logistics program quietly turns them back into recovered dollars.",
    category: "Logistics",
    date: "Jun 02, 2025",
    image: "https://images.unsplash.com/photo-1580674285054-bed31e145f59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxwYWNraW5nJTIwcmV0dXJuc3xlbnwwfHx8fDE3ODcwOTE0NDV8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 3,
    title: "Selling Off Stock Without Hurting Your Brand",
    excerpt: "Liquidating doesn't have to cheapen your name. See how quiet, offline channels keep your pricing and reputation intact.",
    category: "Strategy",
    date: "May 21, 2025",
    image: "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsaXF1aWRhdGlvbiUyMGJveGVzfGVufDB8fHx8MTc4NzA5MTQ0NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 4,
    title: "Understanding Inventory in a Bankruptcy",
    excerpt: "Bankruptcy sales rarely wait. Get to grips with the process, the key parties, and how creditors can walk away with more.",
    category: "Bankruptcy",
    date: "May 09, 2025",
    image: "https://images.pexels.com/photos/8815849/pexels-photo-8815849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: 5,
    title: "A Retailer's Checklist for Closing a Store",
    excerpt: "Winding down a location? This practical checklist keeps pack-outs, transport, and payment moving without the stress.",
    category: "Retail",
    date: "Apr 27, 2025",
    image: "https://images.unsplash.com/photo-1714627798569-b3e36d409c4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxmb3JrbGlmdCUyMHdhcmVob3VzZXxlbnwwfHx8fDE3ODcwOTE0NzV8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 6,
    title: "Overstock Isn't a Loss \u2014 It's Locked-Up Cash",
    excerpt: "That surplus on your shelves is really trapped capital. Here's how one clean sale can free it up and put the money to work again.",
    category: "Finance",
    date: "Apr 10, 2025",
    image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMHRydWNrfGVufDB8fHx8MTc4NzA5MTQ3NXww&ixlib=rb-4.1.0&q=85",
  },
];

export const HERO_WORDS = [
  "Excess", "Overstock", "Distressed", "Closeout", "Unsaleable", "Abandoned", "Damaged", "Rejected",
];

// --- Blended content: buyers & sellers network model ---
export const SELL_OPTIONS = [
  {
    icon: Handshake,
    title: "Sell to Us Directly",
    text: "Need speed and certainty? We buy your surplus outright and wire payment on receipt, so the cash lands in your account without delay.",
  },
  {
    icon: Percent,
    title: "Revenue-Share Placement",
    text: "Chasing the highest possible return? We match your stock with trusted buyers in our network and split the proceeds \u2014 more flexibility, more upside.",
  },
];

export const DIFFERENTIATORS = [
  { icon: Zap, title: "Fastest Turnaround", text: "We review deals day and night, so nothing sits waiting when you need to move." },
  { icon: Globe, title: "Strong Global Network", text: "A deep bench of vetted surplus and liquidation buyers reaching across every major category." },
  { icon: Eye, title: "100% Transparency", text: "Straight talk from the first call to the final wire \u2014 you always know where things stand." },
  { icon: ShieldCheck, title: "Complete Brand Protection", text: "Each sale is placed quietly to safeguard your pricing and your primary sales channels." },
];

export const NETWORK_POINTS = [
  {
    icon: Globe,
    title: "Reach Around the World",
    text: "Our web of buyers and sellers stretches across borders and product lines, so your stock lands with the right buyer quickly.",
  },
  {
    icon: Handshake,
    title: "Relationships First",
    text: "We play the long game, earning trust instead of chasing quick wins \u2014 which is why buyers keep coming back and clients keep winning.",
  },
  {
    icon: Network,
    title: "Powered by Data",
    text: "Direct lines to thousands of vendors, backed by current market databases, let us line up the right deal fast.",
  },
];

export const SELLER_STATS = [
  { value: "$500M+", label: "Excess Inventory Moved" },
  { value: "15+ Yrs", label: "Secondary-Market Experience" },
  { value: "750+", label: "Brands & Retailers Served" },
  { value: "2,000+", label: "Deals Completed" },
];

// Fields used by the Submit Inventory page
export const INVENTORY_CONDITIONS = [
  "New / Overstock", "Closeout", "Shelf Pulls", "Customer Returns",
  "Refurbished", "Salvage / Damaged", "Discontinued", "Short-Dated",
];
